"use client";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useParams } from "next/navigation";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import useProductReview from "@/app/lib/features/productReview/productReview.hook";
import useNotFound from "@/app/lib/features/notFound/notFound.hook";
import useAuth from "@/app/lib/features/auth/auth.hook";
import useSettings from "@/app/lib/features/settings/settings.hook";
import { trackViewItem } from "@/app/lib/tracking/events";
import { resolveGender } from "@/app/lib/tracking/gender";
import { trackProductView } from "@/app/lib/tracking/interactions";
import ImageSlider from "@/app/components/common/product-detail/imageSlider/ImageSlider";
import ProductSidebar from "@/app/components/common/product-detail/productSidebar/ProductSidebar";
import ProductHeader from "@/app/components/common/ProductHeader";
import ProductInformation from "@/app/components/common/product-detail/productInformation/ProductInformation";
import FooterProductSections from "@/app/components/common/sections/FooterProductSections";
import ProductAboutSection from "@/app/components/common/sections/ProductAboutSection";
import ProductReviewsSection from "@/app/components/common/sections/ProductReviewsSection";
import ProductDetailBreadcrumb from "@/app/components/elements/breadcrumb/ProductDetailBreadcrumb";
import ProductDetailSkeleton from "@/app/components/elements/skeletons/ProductDetailSkeleton";
import ProductMobileBar from "@/app/components/common/product-detail/ProductMobileBar";
import NotFoundPage from "../not-found/NotFoundPage";
import { ProductDetailData } from "@/app/lib/features/productDetail/productDetail.types";
import type { ReviewListItem } from "@/app/lib/features/productReview/productReview.types";
import { ProductDetailInitialProvider } from "@/app/lib/features/productDetail/productDetail.context";
import { preloadFromSSR } from "@/app/lib/features/productDetail/productDetail.slice";
import { consumeBackForwardNavigation } from "@/app/lib/navigationType";

interface ProductDetailClientProps {
  slug: string;
  initialData: ProductDetailData;
  /**
   * Değerlendirme bölümünün ilk boyamada basacağı yorumlar — sunucuda
   * çekiliyor (page.tsx, JSON-LD'nin `review` alanıyla aynı kaynak).
   *
   * Bu prop olmadan bölüm ilk HTML'de boş kalıyor ve yorumlar hidrasyondan
   * sonra beliriyordu; footer'ın kaymasının (CLS) ikinci kaynağı buydu.
   */
  initialReviews?: ReviewListItem[];
}

/**
 * Son gösterilen ürünün slug'ı — MODÜL seviyesinde tutuluyor.
 *
 * Bileşen içi ref olamaz: ürünler arası geçişte bu bileşen remount olabiliyor
 * (aynı route segmenti + Suspense sınırı), remount olan bir ref her seferinde
 * sıfırlanır ve "değişti mi?" karşılaştırması hep false çıkardı. Modül değişkeni
 * belge ömrü boyunca yaşadığı için mount/remount fark etmez.
 *
 * Belge yenilendiğinde (tam sayfa yükleme) null'a döner — ilk açılışta
 * kaydırma yapılmaması bu sayede.
 */
let lastVisitedProductSlug: string | null = null;

// Inner component — must be rendered inside ProductDetailInitialProvider so that
// useProductDetail() → useProductDetailInitial() can read the context and return
// initialData.product as a fallback on first render (before the useEffect dispatch).
// Without this split, the hook runs outside the provider and context returns null,
// causing a skeleton flash on every page load.
const ProductDetailInner = ({
  slug: initialSlug,
  initialData,
  initialReviews,
}: ProductDetailClientProps) => {
  const params = useParams();
  const slug = (params.slug as string) || initialSlug;

  const { product, isError, status, isColorChanging, currentSlug, fetchProductDetail, reset, dispatch } =
    useProductDetail();
  const { reset: resetReview } = useProductReview();

  const { currentSlug: notFoundSlug, fetchNotFound } = useNotFound();
  const { isAuthenticated } = useAuth();
  const { settings } = useSettings();
  const trackedProductRef = useRef<string | null>(null);

  const loadedSlugRef = useRef<string | null>(null);
  const authFetchedRef = useRef(false);

  // SSR initialData'yı Redux'a yükle — her yeni initialData geldiğinde (renk değişimi dahil)
  useEffect(() => {
    if (loadedSlugRef.current === initialSlug) return;

    loadedSlugRef.current = initialSlug;
    authFetchedRef.current = false;

    resetReview();
    dispatch(preloadFromSSR({ data: initialData, slug: initialSlug }));
  }, [initialSlug, initialData, dispatch, resetReview]);

  // Auth varsa SSR verisini client'tan tekrar fetch et (is_following vb. auth-dependent alanlar).
  // silentRefetch flag'i ile fetch yapılır — status "loading"e çekilmez, mevcut içerik korunur.
  useEffect(() => {
    if (
      isAuthenticated &&
      loadedSlugRef.current === slug &&
      !authFetchedRef.current
    ) {
      authFetchedRef.current = true;
      fetchProductDetail(slug, undefined, true);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Slug farklılaştıysa ve SSR initialData gelmemişse (SlugPageClient üzerinden girilmişse) fetch yap.
  // loadedSlugRef doluysa bu mount'ta initialData zaten yüklendi — idle olsa bile fetch tetikleme.
  // initialData varsa SSR verisi mevcut — useEffect #1 zaten yükledi, burada fetch gereksiz ve tehlikeli
  // (reset() → status=idle → bu guard pas geçer → normal fetch → status=loading → flash).
  useEffect(() => {
    if (status !== "idle") return;
    if (loadedSlugRef.current) return;
    if (initialData) return;

    loadedSlugRef.current = slug;
    fetchProductDetail(slug);
  }, [slug, status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isError && slug !== notFoundSlug) {
      fetchNotFound(slug);
    }
  }, [isError]); // eslint-disable-line react-hooks/exhaustive-deps

  // BAŞKA BİR ÜRÜNE GEÇİLDİĞİNDE SAYFAYI BAŞA AL.
  //
  // İki giriş yolu da kendiliğinden kaydırmıyordu ve sebepleri AYRI:
  //
  // 1) RENK DEĞİŞİMİ — ProductInformation.handleColorChange
  //    `window.history.pushState(null, "", `/${slug}`)` çağırıyor. Next bu
  //    metodu yamalayıp router state'ini günceller (useParams yeni slug'ı
  //    döndürür) ama bu bir NAVİGASYON değil; Next'in scroll sıfırlaması
  //    bilerek çalışmaz. İçerik yenilenir, kaydırma konumu olduğu yerde kalır.
  //
  // 2) ALTTAKİ ÖNERİ CAROUSEL'LERİNDEN ÜRÜNE TIKLAMA — gerçek <Link>
  //    navigasyonu, yani Next'in scroll handler'ı ÇALIŞIR ama kendini iptal
  //    eder. Handler, yeni segmentin ilk DOM düğümünden başlayıp yüksekliği 0
  //    olanları atlar; sıradaki kardeş yoksa hiç kaydırmadan çıkar. Bu sayfada
  //    segmentin çocukları sırasıyla:
  //      • <ImagePreload>       → null döndürüyor (react-dom preload)
  //      • <script ld+json>     → getBoundingClientRect().height === 0
  //      • <Suspense fallback={null}> → askıdayken DOM'a hiçbir şey basmıyor
  //    Yani commit anında "atlanmayacak" tek bir eleman bile bulamıyor.
  //
  // fallback'i skeleton'a çevirmek 2'yi çözerdi ama 1'i çözmez ve
  // ProductDetailSkeleton flash'ını geri getirirdi (bkz. ProductDetailInner
  // üstündeki not). Bu yüzden kaydırma açıkça burada yapılıyor: slug her
  // değiştiğinde, giriş yolu ne olursa olsun.
  // Ölçüt `useParams()` DEĞİL, Redux'taki `currentSlug`. Sebebi 1. maddede:
  // Next'in pushState yaması router'ın URL'ini günceller ama sunucu bileşenlerini
  // yeniden çalıştırıp dinamik segmenti (params.slug) yeniden türetmez — renk
  // değişiminde useParams eski slug'da kalabilir. `currentSlug` ise her iki
  // yolda da yazılıyor: preloadFromSSR (Link navigasyonu) ve
  // getProductDetail.fulfilled (renk değişimi). Tek güvenilir sinyal bu.
  useEffect(() => {
    if (!currentSlug) return;

    const previousSlug = lastVisitedProductSlug;
    lastVisitedProductSlug = currentSlug;

    // İlk yükleme veya aynı ürün — kaydırmaya gerek yok. Tam sayfa yüklemesinde
    // tarayıcı zaten tepede; buradan scrollTo çağırmak tarayıcının kendi
    // restorasyonunu da ezerdi.
    if (previousSlug === null || previousSlug === currentSlug) return;

    // Geri/ileri: kullanıcı bıraktığı yere dönmeyi bekler, başa atmak yanlış.
    if (consumeBackForwardNavigation()) return;

    window.scrollTo({ top: 0, behavior: "instant" });
  }, [currentSlug]);

  // Ürün detay sayfasında normal header'ı gizle — useLayoutEffect ile paint öncesi eklenir (flash önlenir)
  useLayoutEffect(() => {
    document.body.classList.add("hide-main-header", "has-mobile-bar");
    return () => {
      document.body.classList.remove("hide-main-header", "has-mobile-bar");
    };
  }, []);

  // Cleanup: farklı sayfaya gidince Redux'u ve review'ı temizle.
  // useEffect #3'teki loadedSlugRef guard sayesinde reset() → status="idle" olsa bile
  // fetchProductDetail tetiklenmez (loadedSlugRef dolu olduğu sürece).
  useEffect(() => {
    return () => {
      reset();
      resetReview();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // trackViewItem — ürün ilk yüklendiğinde veya varyant değiştiğinde bir kez tetikle
  useEffect(() => {
    if (!product) return;
    const key = `${product.id}-${product.variant_id}`;
    if (trackedProductRef.current === key) return;
    trackedProductRef.current = key;

    const selectedVariant = product.variants_by_color?.find(
      (v) => v.is_selected,
    );
    const selectedSize = selectedVariant?.sizes?.find((s) => s.is_selected);
    const price = selectedSize?.price ?? product.price;

    // discount: birim başına indirim tutarı. Beden bazlı list_price API'de yok,
    // ürün seviyesindeki list_price kullanılıyor; negatife düşmesin diye clamp.
    const discount = Math.max(0, (product.list_price ?? price) - price);
    // item_variant: renk + beden tek string (GA4 konvansiyonu).
    const itemVariant =
      [selectedVariant?.color_name, selectedSize?.size]
        .filter(Boolean)
        .join(" / ") || undefined;
    // gender: API ürün yanıtında yok → kategori slug'larından türetiliyor
    // (bkz. tracking/gender.ts). Tanınmayan taksonomide undefined kalır.
    const gender = resolveGender(
      product.category_sub_group?.slug,
      product.category_group?.slug,
      product.category?.slug,
      product.slug,
    );

    trackViewItem(
      {
        value: price,
        items: [
          {
            item_id: product.id,
            // Meta/TikTok katalog kimliği = feed'deki g:id = beden seviyesi
            // variant_id. ProductDetail tipinde bu alan sizes[n].id (listeleme
            // tipindeki sizes[n].variant_id ile aynı ID uzayı, farklı ad).
            // Beden seçili değilse ürünün varsayılan varyantına düşer.
            content_id: selectedSize?.id ?? product.variant_id,
            item_name: product.name,
            item_brand: product.brand_name ?? undefined,
            affiliation: product.seller?.name,
            item_category: product.category?.name,
            price,
            quantity: 1,
            // Canlıdaki (eski) view_item ile aynı alan seti:
            discount,
            index: 0, // detay sayfasında tek ürün
            item_variant: itemVariant,
            gender,
          },
        ],
      },
      settings?.google_ads_view_item_label || undefined,
    );
    trackProductView(product.id);
  }, [product?.id, product?.variant_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isError) return <NotFoundPage />;

  const reduxProduct =
    product && (isColorChanging || product.slug === initialSlug || product.slug === slug || (currentSlug !== null && product.slug === currentSlug))
      ? product
      : null;
  const freshProduct = reduxProduct ?? initialData?.product;
  const displayProduct = freshProduct;

  if (!displayProduct) return <ProductDetailSkeleton />;

  return (
    <div className="container-minify">
      <ProductHeader />
      <ProductDetailBreadcrumb />
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 xl:gap-8">
        <div className="w-full lg:w-2/5 xl:w-1/2 2xl:w-2/5 lg:sticky lg:top-4 lg:self-start shrink-0">
          <ImageSlider />
        </div>
        <div className="flex-1 min-w-0 lg:w-2/5 xl:w-1/3 2xl:w-2/5">
          <ProductInformation />
        </div>
        <div className="w-full lg:w-1/5 xl:w-1/7 2xl:w-1/6 hidden lg:block shrink-0">
          <ProductSidebar />
        </div>
      </div>
      <ProductMobileBar />
      <div className="space-y-6 md:space-y-8 lg:space-y-10 my-6 md:my-8 lg:my-10">
        <FooterProductSections />
        <ProductReviewsSection initialReviews={initialReviews} />
        <ProductAboutSection />
      </div>
    </div>
  );
};

const ProductDetailClient = ({
  slug,
  initialData,
  initialReviews,
}: ProductDetailClientProps) => (
  <ProductDetailInitialProvider initialData={initialData}>
    <ProductDetailInner
      slug={slug}
      initialData={initialData}
      initialReviews={initialReviews}
    />
  </ProductDetailInitialProvider>
);

export default ProductDetailClient;